import Delaunator from "delaunator";

export function getTerrainType(data) {
  const raw = String(data?.type || "").toLowerCase();
  if (raw === "tin") return "tin";
  if (raw === "grid") return "grid";
  if (Number.isFinite(Number(data?.width)) && Number.isFinite(Number(data?.height)) && data?.values) return "grid";
  if (data?.vertices && data?.triangles) return "tin";
  return "";
}

export function getTerrainExtents(data) {
  if (!data || typeof data !== "object") return null;
  const minX = Number(data.minX);
  const maxX = Number(data.maxX);
  const minZ = Number(data.minZ);
  const maxZ = Number(data.maxZ);
  if (![minX, maxX, minZ, maxZ].every(Number.isFinite)) return null;
  return { minX, maxX, minZ, maxZ };
}

export function sampleTerrainHeight(data, x, z) {
  const type = getTerrainType(data);
  if (type === "grid") return sampleGridHeight(data, x, z);
  if (type === "tin") return sampleTinHeight(data, x, z);
  return null;
}

export function sampleGridHeight(grid, x, z) {
  if (!grid || !Number.isFinite(grid.width) || !Number.isFinite(grid.height) || !grid.values) {
    return null;
  }
  const ext = getTerrainExtents(grid);
  if (!ext || !(x >= ext.minX && x <= ext.maxX && z >= ext.minZ && z <= ext.maxZ)) {
    return null;
  }

  const nx = Math.max(2, Number(grid.width));
  const nz = Math.max(2, Number(grid.height));
  const tx = ((x - ext.minX) / Math.max(1e-6, ext.maxX - ext.minX)) * (nx - 1);
  const tz = ((z - ext.minZ) / Math.max(1e-6, ext.maxZ - ext.minZ)) * (nz - 1);
  const x0 = Math.max(0, Math.min(nx - 1, Math.floor(tx)));
  const z0 = Math.max(0, Math.min(nz - 1, Math.floor(tz)));
  const x1 = Math.min(nx - 1, x0 + 1);
  const z1 = Math.min(nz - 1, z0 + 1);
  const fx = tx - x0;
  const fz = tz - z0;

  const v00 = getGridValue(grid.values, nx, x0, z0);
  const v10 = getGridValue(grid.values, nx, x1, z0);
  const v01 = getGridValue(grid.values, nx, x0, z1);
  const v11 = getGridValue(grid.values, nx, x1, z1);
  const top = v00 + (v10 - v00) * fx;
  const bottom = v01 + (v11 - v01) * fx;
  return top + (bottom - top) * fz;
}

function getGridValue(values, width, x, z) {
  return Number(values[z * width + x]);
}

export function sampleTinHeight(tin, x, z) {
  if (!tin?.vertices || !tin?.triangles) return null;
  const ext = getTerrainExtents(tin);
  if (!ext || !(x >= ext.minX && x <= ext.maxX && z >= ext.minZ && z <= ext.maxZ)) {
    return null;
  }
  const bins = tin.binOffsets && tin.binTriangleIndices ? collectBinCandidates(tin, x, z) : null;
  if (Array.isArray(bins)) {
    if (!bins.length) {
      return null;
    }
    const hit = sampleTinCandidates(tin, bins, x, z);
    return Number.isFinite(hit) ? hit : null;
  }
  const fallback = sampleTinCandidates(tin, null, x, z);
  return Number.isFinite(fallback) ? fallback : null;
}

function sampleTinCandidates(tin, candidates, x, z) {
  const vertices = tin.vertices;
  const triangles = tin.triangles;
  const triCount = Math.floor(triangles.length / 3);
  const list = Array.isArray(candidates) && candidates.length ? candidates : null;
  const count = list ? list.length : triCount;
  for (let idx = 0; idx < count; idx += 1) {
    const triIndex = list ? list[idx] : idx;
    if (!Number.isFinite(triIndex) || triIndex < 0 || triIndex >= triCount) continue;
    const base = triIndex * 3;
    const i0 = Number(triangles[base]) * 3;
    const i1 = Number(triangles[base + 1]) * 3;
    const i2 = Number(triangles[base + 2]) * 3;
    const ax = Number(vertices[i0]);
    const ay = Number(vertices[i0 + 1]);
    const az = Number(vertices[i0 + 2]);
    const bx = Number(vertices[i1]);
    const by = Number(vertices[i1 + 1]);
    const bz = Number(vertices[i1 + 2]);
    const cx = Number(vertices[i2]);
    const cy = Number(vertices[i2 + 1]);
    const cz = Number(vertices[i2 + 2]);
    const bary = barycentricXZ(x, z, ax, az, bx, bz, cx, cz);
    if (!bary) continue;
    return ay * bary.u + by * bary.v + cy * bary.w;
  }
  return null;
}

function barycentricXZ(px, pz, ax, az, bx, bz, cx, cz) {
  const v0x = bx - ax;
  const v0z = bz - az;
  const v1x = cx - ax;
  const v1z = cz - az;
  const v2x = px - ax;
  const v2z = pz - az;
  const den = (v0x * v1z) - (v1x * v0z);
  if (Math.abs(den) < 1e-9) return null;
  const invDen = 1 / den;
  const v = ((v2x * v1z) - (v1x * v2z)) * invDen;
  const w = ((v0x * v2z) - (v2x * v0z)) * invDen;
  const u = 1 - v - w;
  const eps = -1e-6;
  if (u < eps || v < eps || w < eps) return null;
  return { u, v, w };
}

function collectBinCandidates(tin, x, z) {
  const cols = Math.max(1, Math.floor(Number(tin.binCols) || 1));
  const rows = Math.max(1, Math.floor(Number(tin.binRows) || 1));
  const ext = getTerrainExtents(tin);
  if (!ext) return null;
  const tx = (x - ext.minX) / Math.max(1e-6, ext.maxX - ext.minX);
  const tz = (z - ext.minZ) / Math.max(1e-6, ext.maxZ - ext.minZ);
  const col = Math.max(0, Math.min(cols - 1, Math.floor(tx * cols)));
  const row = Math.max(0, Math.min(rows - 1, Math.floor(tz * rows)));
  const out = [];
  for (let dr = -1; dr <= 1; dr += 1) {
    const rr = row + dr;
    if (rr < 0 || rr >= rows) continue;
    for (let dc = -1; dc <= 1; dc += 1) {
      const cc = col + dc;
      if (cc < 0 || cc >= cols) continue;
      const cellIndex = rr * cols + cc;
      const start = Number(tin.binOffsets[cellIndex]);
      const end = Number(tin.binOffsets[cellIndex + 1]);
      for (let i = start; i < end; i += 1) {
        out.push(Number(tin.binTriangleIndices[i]));
      }
    }
  }
  return out;
}

export function buildTinTerrainFromPoints(points, { binTarget = 48 } = {}) {
  const valid = Array.isArray(points)
    ? points.map((p) => ({
      x: Number(p?.x),
      y: Number(p?.y),
      z: Number(p?.z),
    })).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y) && Number.isFinite(p.z))
    : [];
  if (valid.length < 3) {
    throw new Error("Need at least three valid points to build TIN terrain.");
  }

  const delaunay = Delaunator.from(valid, (p) => p.x, (p) => p.z);
  const rawTriangles = delaunay.triangles || [];
  if (rawTriangles.length < 3) {
    throw new Error("Unable to triangulate terrain points.");
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const vertices = new Float32Array(valid.length * 3);
  for (let i = 0; i < valid.length; i += 1) {
    const p = valid[i];
    const j = i * 3;
    vertices[j] = p.x;
    vertices[j + 1] = p.y;
    vertices[j + 2] = p.z;
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const triangles = [];
  for (let i = 0; i < rawTriangles.length; i += 3) {
    const ia = Number(rawTriangles[i]);
    const ib = Number(rawTriangles[i + 1]);
    const ic = Number(rawTriangles[i + 2]);
    if (!Number.isFinite(ia) || !Number.isFinite(ib) || !Number.isFinite(ic)) continue;
    const area = Math.abs(
      ((valid[ib].x - valid[ia].x) * (valid[ic].z - valid[ia].z))
      - ((valid[ic].x - valid[ia].x) * (valid[ib].z - valid[ia].z)),
    ) * 0.5;
    if (area <= 1e-8) continue;
    triangles.push(ia, ib, ic);
  }
  if (triangles.length < 3) {
    throw new Error("TIN terrain triangulation was degenerate.");
  }

  const terrain = {
    type: "tin",
    minX,
    maxX,
    minZ,
    maxZ,
    minY,
    maxY,
    vertices,
    triangles: Uint32Array.from(triangles),
  };
  const bins = buildTinSpatialBins(terrain, binTarget);
  return {
    ...terrain,
    ...bins,
  };
}

export function buildTinSpatialBins(tin, binTarget = 48) {
  const ext = getTerrainExtents(tin);
  if (!ext) {
    return { binCols: 1, binRows: 1, binOffsets: Uint32Array.from([0, 0]), binTriangleIndices: new Uint32Array(0) };
  }
  const triCount = Math.floor((tin.triangles?.length || 0) / 3);
  const cols = Math.max(1, Math.min(256, Math.round(Number(binTarget) || 48)));
  const spanX = Math.max(1e-6, ext.maxX - ext.minX);
  const spanZ = Math.max(1e-6, ext.maxZ - ext.minZ);
  const aspect = spanZ / spanX;
  const rows = Math.max(1, Math.min(256, Math.round(cols * aspect)));
  const buckets = Array.from({ length: cols * rows }, () => []);
  const vertices = tin.vertices;
  const triangles = tin.triangles;
  for (let triIndex = 0; triIndex < triCount; triIndex += 1) {
    const base = triIndex * 3;
    const i0 = Number(triangles[base]) * 3;
    const i1 = Number(triangles[base + 1]) * 3;
    const i2 = Number(triangles[base + 2]) * 3;
    const minTriX = Math.min(vertices[i0], vertices[i1], vertices[i2]);
    const maxTriX = Math.max(vertices[i0], vertices[i1], vertices[i2]);
    const minTriZ = Math.min(vertices[i0 + 2], vertices[i1 + 2], vertices[i2 + 2]);
    const maxTriZ = Math.max(vertices[i0 + 2], vertices[i1 + 2], vertices[i2 + 2]);
    const c0 = clampInt(Math.floor(((minTriX - ext.minX) / spanX) * cols), 0, cols - 1);
    const c1 = clampInt(Math.floor(((maxTriX - ext.minX) / spanX) * cols), 0, cols - 1);
    const r0 = clampInt(Math.floor(((minTriZ - ext.minZ) / spanZ) * rows), 0, rows - 1);
    const r1 = clampInt(Math.floor(((maxTriZ - ext.minZ) / spanZ) * rows), 0, rows - 1);
    for (let row = r0; row <= r1; row += 1) {
      for (let col = c0; col <= c1; col += 1) {
        buckets[row * cols + col].push(triIndex);
      }
    }
  }

  const offsets = new Uint32Array((cols * rows) + 1);
  let total = 0;
  for (let i = 0; i < buckets.length; i += 1) {
    offsets[i] = total;
    total += buckets[i].length;
  }
  offsets[buckets.length] = total;
  const indices = new Uint32Array(total);
  let cursor = 0;
  for (let i = 0; i < buckets.length; i += 1) {
    const bucket = buckets[i];
    for (let j = 0; j < bucket.length; j += 1) {
      indices[cursor] = bucket[j];
      cursor += 1;
    }
  }
  return {
    binCols: cols,
    binRows: rows,
    binOffsets: offsets,
    binTriangleIndices: indices,
  };
}

function clampInt(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
